import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workerTerm } from "../utils/workerTerm";

// 작업 배정 — 관제담당자 엑셀 업로드 + 달력/목록 조회.
//   백엔드 API: POST /api/schedule/upload (multipart, field=file)
//              GET  /api/schedule/calendar?month=YYYY-MM  → {days:{date:count}}
//              GET  /api/schedule?date=YYYY-MM-DD         → {schedules:[...]}
// 업로드는 관리자/본사관리자만(백엔드에서도 재검증). 목록/달력은 등급별 스코프.

const UPLOAD_ROLES = new Set(["SUPER_ADMIN", "ADMIN", "HQ_MANAGER"]);
const WEEK = ["일", "월", "화", "수", "목", "금", "토"];
const STATUS_LABEL = {
  assigned: "배정", started: "진행중", done_req: "완료요청", completed: "완료",
};
const STATUS_BADGE = {
  assigned: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
  started: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  done_req: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
};

function pad2(n) { return String(n).padStart(2, "0"); }
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}
function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}
function monthCells(ym) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1).getDay();
  const last = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i += 1) cells.push(null);
  for (let d = 1; d <= last; d += 1) cells.push(`${ym}-${pad2(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function Card({ title, right, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-white p-4 shadow-md dark:bg-[#1F2A3B] ${className}`}>
      {(title || right) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title && <h4 className="text-base font-bold">{title}</h4>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export default function WorkSchedulePage({ me }) {
  const canUpload = UPLOAD_ROLES.has(String(me?.roleCode || "").toUpperCase());

  const [ym, setYm] = useState(thisMonth());
  const [counts, setCounts] = useState({});
  const [selDate, setSelDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [delayed, setDelayed] = useState([]);
  const [delayedOpen, setDelayedOpen] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const loadCalendar = useCallback(async (month) => {
    try {
      const r = await fetch(`/api/schedule/calendar?month=${encodeURIComponent(month)}`, { credentials: "include" });
      const j = await r.json();
      setCounts(j?.days || {});
    } catch (e) {
      setCounts({});
    }
  }, []);

  const loadList = useCallback(async (date) => {
    setLoading(true);
    setMsg("");
    try {
      const r = await fetch(`/api/schedule?date=${encodeURIComponent(date)}`, { credentials: "include" });
      const j = await r.json();
      if (!r.ok || !j?.success) { setMsg(j?.message || "조회 오류"); setRows([]); }
      else setRows(Array.isArray(j.schedules) ? j.schedules : []);
    } catch (e) {
      setMsg("조회 중 오류가 발생했습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDelayed = useCallback(async (date) => {
    try {
      const r = await fetch(`/api/schedule/delayed?date=${encodeURIComponent(date)}`, { credentials: "include" });
      const j = await r.json();
      setDelayed(Array.isArray(j?.items) ? j.items : []);
    } catch (e) {
      setDelayed([]);
    }
  }, []);

  useEffect(() => { loadCalendar(ym); }, [ym, loadCalendar]);
  useEffect(() => { loadList(selDate); loadDelayed(selDate); }, [selDate, loadList, loadDelayed]);

  // 미시작 목록 자동 갱신 — 작업 시작(대기→작업중) 시 '미해결' 카운터가 알아서 줄도록 30초 폴링.
  useEffect(() => {
    const t = setInterval(() => loadDelayed(selDate), 30000);
    return () => clearInterval(t);
  }, [selDate, loadDelayed]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setUploadResult({ ok: false, text: "엑셀 파일을 선택하세요." }); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/schedule/upload", { method: "POST", credentials: "include", body: fd });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        setUploadResult({ ok: false, text: j?.message || "업로드 실패" });
      } else {
        setUploadResult({
          ok: true,
          text: `등록 ${j.inserted}건 · 중복건너뜀 ${j.skipped}건 (총 ${j.total}건 인식)`,
          columns: j.mappedColumns,
        });
        if (fileRef.current) fileRef.current.value = "";
        loadCalendar(ym);
        loadList(selDate);
      }
    } catch (e) {
      setUploadResult({ ok: false, text: "업로드 중 오류가 발생했습니다." });
    } finally {
      setUploading(false);
    }
  };

  const cells = useMemo(() => monthCells(ym), [ym]);
  const monthTotal = useMemo(
    () => Object.values(counts).reduce((s, n) => s + Number(n || 0), 0),
    [counts]
  );

  return (
    <div className="flex flex-col gap-3">
      {/* 업로드 (관리자/본사관리자만) */}
      {canUpload && (
        <Card title="작업 배정 엑셀 업로드">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-white hover:file:bg-blue-700 dark:text-gray-200 lg:w-auto"
            />
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading}
              className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? "업로드 중…" : "업로드"}
            </button>
            <a
              href="/work-schedule-template.xlsx"
              download="작업배정_양식.xlsx"
              className="rounded-xl border border-blue-600 px-4 py-2.5 text-center text-sm font-semibold text-blue-700 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-white/5"
            >
              양식 다운로드
            </a>
          </div>
          <div className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
            <b>인식 컬럼:</b> 작업일 · 약속시간 · 납품번호 · 차량번호 · 고객명 · 설치주소 · 소속지점 · 설치기사 · 작업유형(실외/용접) · 고객요청사항
            <br />
            헤더 이름은 유연하게 인식돼요(예: “주소”=“설치주소”). “설치기사”가 등록된 {workerTerm()} 이름과 같으면 그 {workerTerm()}에게 자동 배정됩니다.
          </div>
          {uploadResult && (
            <div className={`mt-3 rounded-xl px-3 py-2 text-sm ${uploadResult.ok
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
              : "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300"}`}>
              {uploadResult.text}
              {uploadResult.ok && uploadResult.columns?.length ? (
                <span className="ml-1 text-xs opacity-80">· 인식 컬럼: {uploadResult.columns.join(", ")}</span>
              ) : null}
            </div>
          )}
        </Card>
      )}

      {/* 작업 미시작 알림 (예정시간+허용시간 초과 & 알림 발송된 건) */}
      {delayed.length > 0 && (
        <Card
          title={
            <button
              type="button"
              onClick={() => setDelayedOpen((o) => !o)}
              className="flex items-center gap-2 text-red-600 dark:text-red-400"
            >
              <span className="text-xs">{delayedOpen ? "▾" : "▸"}</span>
              <span>⏰ 작업 미시작 알림</span>
            </button>
          }
          right={
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-red-500">
                미해결 {delayed.filter((d) => d.stillPending).length}건 / 총 {delayed.length}건
              </span>
              {delayedOpen && (
                <button
                  type="button"
                  onClick={() => loadDelayed(selDate)}
                  className="rounded-lg bg-gray-200 px-2.5 py-1 text-xs dark:bg-gray-700"
                >
                  새로고침
                </button>
              )}
              <button
                type="button"
                onClick={() => setDelayedOpen((o) => !o)}
                className="rounded-lg bg-gray-200 px-2.5 py-1 text-xs dark:bg-gray-700"
              >
                {delayedOpen ? "접기" : "펼치기"}
              </button>
            </div>
          }
          className="border border-red-200 dark:border-red-500/30"
        >
          {delayedOpen && (
          <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="py-2 pr-3">예정</th>
                  <th className="py-2 pr-3">담당 {workerTerm()}</th>
                  <th className="py-2 pr-3">고객</th>
                  <th className="py-2 pr-3">현장 주소</th>
                  <th className="py-2 pr-3">지점</th>
                  <th className="py-2 pr-3">상태</th>
                  <th className="py-2 pr-3">알림시각</th>
                  <th className="py-2 pr-3">연기 사유</th>
                </tr>
              </thead>
              <tbody>
                {delayed.map((d) => (
                  <tr key={d.ScheduleIdx} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-3 font-semibold">{d.AppointmentTime || "-"}</td>
                    <td className="py-2 pr-3 font-semibold">{d.AssignedName || "-"}</td>
                    <td className="py-2 pr-3">{d.CustomerName || "-"}</td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{d.Address || "-"}</td>
                    <td className="py-2 pr-3">{d.BranchName || "-"}</td>
                    <td className="py-2 pr-3">
                      {d.stillPending ? (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:bg-red-500/15 dark:text-red-300">
                          미시작
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 dark:bg-slate-500/15 dark:text-slate-300">
                          이후 시작
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{d.delayNotifiedAt || "-"}</td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{d.RescheduleReqReason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            예정시간이 지나도 작업 시작이 확인되지 않아 관리자에게 알림이 발송된 건입니다. “미시작”은 아직 시작 안 됨, “이후 시작”은 알림 후 시작됨.
          </div>
          </>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(320px,0.9fr)_1.6fr]">
        {/* 달력 */}
        <Card
          title="작업 달력"
          right={
            <div className="flex items-center gap-2">
              <button onClick={() => setYm(shiftMonth(ym, -1))} className="rounded-lg bg-gray-200 px-2.5 py-1 text-sm dark:bg-gray-700">←</button>
              <span className="min-w-[92px] text-center text-sm font-semibold">{ym.replace("-", ". ")}.</span>
              <button onClick={() => setYm(shiftMonth(ym, 1))} className="rounded-lg bg-gray-200 px-2.5 py-1 text-sm dark:bg-gray-700">→</button>
            </div>
          }
        >
          <div className="mb-1 grid grid-cols-7 text-center text-[11px] text-gray-400">
            {WEEK.map((w, i) => (
              <span key={w} className={i === 0 ? "text-red-400" : i === 6 ? "text-blue-400" : ""}>{w}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, i) => {
              if (!date) return <div key={`e${i}`} />;
              const day = Number(date.slice(-2));
              const c = counts[date] || 0;
              const active = date === selDate;
              return (
                <button
                  key={date}
                  onClick={() => setSelDate(date)}
                  className={`flex h-14 flex-col items-center justify-start rounded-xl border p-1 text-xs transition ${active
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-200 bg-white hover:bg-blue-50 dark:border-gray-700 dark:bg-[#0F172A] dark:hover:bg-white/5"}`}
                >
                  <span className="font-semibold">{day}</span>
                  {c > 0 && (
                    <span className={`mt-1 rounded-full px-1.5 text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-200"}`}>
                      {c}건
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-right text-xs text-gray-500 dark:text-gray-400">이달 총 {monthTotal}건</div>
        </Card>

        {/* 목록 */}
        <Card title={`${selDate} 작업`} right={<span className="text-xs text-gray-500 dark:text-gray-400">{rows.length}건</span>}>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">불러오는 중…</div>
          ) : msg ? (
            <div className="py-10 text-center text-sm text-gray-400">{msg}</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">해당 날짜에 배정된 작업이 없습니다.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="py-2 pr-3">시간</th>
                    <th className="py-2 pr-3">납품번호</th>
                    <th className="py-2 pr-3">차량</th>
                    <th className="py-2 pr-3">고객</th>
                    <th className="py-2 pr-3">현장 주소</th>
                    <th className="py-2 pr-3">지점</th>
                    <th className="py-2 pr-3">담당</th>
                    <th className="py-2 pr-3">유형</th>
                    <th className="py-2 pr-3">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = String(r.Status || "assigned").toLowerCase();
                    return (
                      <tr key={r.ScheduleIdx} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-3 font-semibold">{r.AppointmentTime || "-"}</td>
                        <td className="py-2 pr-3">{r.DeliveryNo || "-"}</td>
                        <td className="py-2 pr-3">{r.VehicleNo || "-"}</td>
                        <td className="py-2 pr-3">{r.CustomerName || "-"}</td>
                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{r.Address || "-"}</td>
                        <td className="py-2 pr-3">{r.BranchName || "-"}</td>
                        <td className="py-2 pr-3">{r.AssignedName || "-"}</td>
                        <td className="py-2 pr-3">{r.WorkType || "-"}</td>
                        <td className="py-2 pr-3">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[st] || STATUS_BADGE.assigned}`}>
                            {STATUS_LABEL[st] || "배정"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
